import {
  getCloudAppState,
  getCloudSortState,
  getCloudViewMode,
  normalizeCloudSortField,
  normalizeCloudViewMode,
  setCloudAppState,
  setCloudSortState,
  setCloudViewMode
} from '@/utils/cloudState'

const COMPONENT_APP_KEYS = {
  PaperSync: 'paper',
  MeetingSync: 'meeting',
  MemoSync: 'memo',
  LearningSync: 'learning',
  KnowledgeBaseSync: 'knowledge',
  DailySync: 'daily'
}

export default {
  data() {
    return {
      cloudListStateRestoring: false
    }
  },
  created() {
    this.restoreCloudListState()
  },
  mounted() {
    if (typeof window !== 'undefined') {
      window.addEventListener('viwoods-cloud-view-mode-change', this.handleCloudViewModeChange)
      window.addEventListener('viwoods-cloud-sort-change', this.handleCloudSortChange)
    }
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('viwoods-cloud-view-mode-change', this.handleCloudViewModeChange)
      window.removeEventListener('viwoods-cloud-sort-change', this.handleCloudSortChange)
    }
  },
  watch: {
    viewMode(value) {
      setCloudViewMode(value)
      this.saveCloudListState()
    },
    paperListResourceId() {
      this.saveCloudListState()
    },
    paperFolderPath: {
      deep: true,
      handler() {
        this.saveCloudListState()
      }
    },
    paperFilter() {
      this.saveCloudListState()
    },
    sortField(value) {
      if (!this.cloudListStateRestoring) {
        setCloudSortState({ sortField: value, sortDesc: this.sortDesc !== false })
      }
      this.saveCloudListState()
    },
    sortDesc(value) {
      if (!this.cloudListStateRestoring) {
        setCloudSortState({ sortField: this.sortField, sortDesc: value === true })
      }
      this.saveCloudListState()
    },
    searchOpen() {
      this.saveCloudListState()
    },
    searchKeyword() {
      this.saveCloudListState()
    },
    dailyActiveTab() {
      this.saveCloudListState()
    },
    dailyPeriod() {
      this.saveCloudListState()
    },
    dailyTodoFinishFilter() {
      this.saveCloudListState()
    },
    dailyViewMode(value) {
      if (!this.cloudListStateRestoring && !(this.isDailyMode && this.isDailyTodoTab)) {
        const viewMode = setCloudViewMode(value, true)
        if ('viewMode' in this && this.viewMode != viewMode) {
          this.viewMode = viewMode
        }
      }
      this.saveCloudListState()
    }
  },
  methods: {
    normalizePaperIdentityText(value) {
      if (value === undefined || value === null) {
        return ''
      }
      return String(value).trim()
    },
    normalizePaperIdentityKey(value) {
      return this.normalizePaperIdentityText(value).toLowerCase().replace(/[\s_\-/.]/g, '')
    },
    getPaperFolderRawValue(source, keys) {
      if (!source || typeof source != 'object') {
        return undefined
      }
      const sourceKeys = Object.keys(source)
      for (let i = 0; i < keys.length; i++) {
        const key = keys[i]
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          return source[key]
        }
      }
      for (let i = 0; i < keys.length; i++) {
        const normalizedKey = this.normalizePaperIdentityKey(keys[i])
        const matchedKey = sourceKeys.find(sourceKey => this.normalizePaperIdentityKey(sourceKey) == normalizedKey)
        if (matchedKey) {
          return source[matchedKey]
        }
      }
      return undefined
    },
    isPaperSentinelResourceId(resourceId) {
      const text = this.normalizePaperIdentityText(resourceId)
      if (!text) {
        return false
      }
      const value = Number(text)
      return Number.isFinite(value) && (value === 0 || value === 5)
    },
    getPaperFolderPathKey(item, index = 0) {
      const raw = item && typeof item == 'object' ? item : {}
      const resourceId = this.normalizePaperIdentityText(this.getPaperFolderRawValue(raw, ['resourceId', 'resource_id', 'resourceID', 'uuid', 'id', 'resourceUuid', 'resource_uuid', 'paperId', 'paper_id', 'dailyId', 'daily_id']))
      const resourceType = this.normalizePaperIdentityText(this.getPaperFolderRawValue(raw, ['resourceType', 'type']))
      const subTab = this.normalizePaperIdentityText(this.getPaperFolderRawValue(raw, ['subTab', 'sub_tab', 'subtab', 'subTabId', 'sub_tab_id', 'dailySubTab', 'daily_sub_tab', 'tab', 'dailyType', 'daily_type']))
      const name = this.normalizePaperIdentityKey(this.getPaperFolderRawValue(raw, ['name', 'title', 'fileName', 'resourceName', 'paperName', 'folderName', 'label', 'displayName']))
      const filePath = this.normalizePaperIdentityKey(this.getPaperFolderRawValue(raw, ['filePath', 'file_path', 'path', 'fileUrl', 'url', 'src']))
      const parts = []
      if (resourceId && !this.isPaperSentinelResourceId(resourceId)) {
        return `rid:${resourceId}`
      }
      if (resourceType) {
        parts.push(`type:${resourceType}`)
      }
      if (subTab) {
        parts.push(`tab:${subTab}`)
      }
      if (name) {
        parts.push(`name:${name}`)
      }
      if (filePath) {
        parts.push(`path:${filePath}`)
      }
      if (parts.length == 0 && resourceId) {
        parts.push(`rid:${resourceId}`)
      }
      if (parts.length == 0) {
        parts.push(`idx:${Math.max(0, Number(index) || 0)}`)
      }
      return parts.join('|')
    },
    normalizePaperFolderPathItem(item, index = 0) {
      const raw = item && typeof item == 'object' ? item : {}
      const name = this.normalizePaperIdentityText(this.getPaperFolderRawValue(raw, ['name', 'title', 'fileName', 'resourceName', 'paperName', 'folderName', 'label', 'displayName']))
      const resourceId = this.normalizePaperIdentityText(this.getPaperFolderRawValue(raw, ['resourceId', 'resource_id', 'resourceID', 'uuid', 'id', 'resourceUuid', 'resource_uuid', 'paperId', 'paper_id', 'dailyId', 'daily_id']))
      const resourceType = this.getPaperFolderRawValue(raw, ['resourceType', 'type'])
      const subTab = this.getPaperFolderRawValue(raw, ['subTab', 'sub_tab', 'subtab', 'subTabId', 'sub_tab_id', 'dailySubTab', 'daily_sub_tab', 'tab', 'dailyType', 'daily_type'])
      const hasResourceType = resourceType !== undefined && resourceType !== null && resourceType !== ''
      const hasSubTab = subTab !== undefined && subTab !== null && subTab !== ''
      if (!name && !resourceId && !hasResourceType && !hasSubTab && !raw.pathKey) {
        return null
      }
      return Object.assign({}, raw, {
        name,
        resourceId,
        resourceType,
        subTab,
        pathKey: raw.pathKey ? this.normalizePaperIdentityText(raw.pathKey) : this.getPaperFolderPathKey(raw, index)
      })
    },
    getPaperItemKey(item, index = 0) {
      const raw = item && typeof item == 'object' ? item : {}
      const resourceId = this.normalizePaperIdentityText(this.getPaperFolderRawValue(raw, ['resourceId', 'resource_id', 'resourceID', 'uuid', 'id', 'resourceUuid', 'resource_uuid', 'paperId', 'paper_id', 'dailyId', 'daily_id']))
      if (resourceId && !this.isPaperSentinelResourceId(resourceId)) {
        return resourceId
      }
      return this.getPaperFolderPathKey(item, index)
    },
    normalizeCloudPaperFolderPath(folderPath) {
      if (!Array.isArray(folderPath)) {
        return []
      }
      return folderPath.map((item, index) => this.normalizePaperFolderPathItem(item, index)).filter(Boolean)
    },
    getCloudListStateAppKey() {
      return this.$options.cloudStateKey || COMPONENT_APP_KEYS[this.$options.name] || ''
    },
    shouldRestoreCloudListAppState() {
      return !!(this.initialRefreshState && typeof this.initialRefreshState == 'object')
    },
    restoreCloudListState() {
      this.cloudListStateRestoring = true
      const appKey = this.getCloudListStateAppKey()
      const shouldRestoreAppState = this.shouldRestoreCloudListAppState()
      const shouldRestoreDailyFilters = shouldRestoreAppState || this.$options.restoreDailyFiltersWithoutPageState === true
      const persistedState = getCloudAppState(appKey) || {}
      const state = shouldRestoreAppState ? persistedState : {}
      const sharedViewMode = getCloudViewMode(this.viewMode)
      this.viewMode = sharedViewMode
      const sortState = getCloudSortState({ sortField: this.sortField, sortDesc: this.sortDesc })
      if ('sortField' in this) {
        this.sortField = sortState.sortField
      }
      if ('sortDesc' in this) {
        this.sortDesc = sortState.sortDesc
      }
      if (shouldRestoreAppState) {
        if ('paperListResourceId' in this && typeof state.paperListResourceId == 'string') {
          this.paperListResourceId = state.paperListResourceId
        }
        if ('paperFolderPath' in this && Array.isArray(state.paperFolderPath)) {
          this.paperFolderPath = this.normalizeCloudPaperFolderPath(state.paperFolderPath)
        }
        if ('paperFilter' in this && ['all', 'favorite', 'deleted'].includes(state.paperFilter)) {
          this.paperFilter = state.paperFilter
        }
        if ('searchOpen' in this && typeof state.searchOpen == 'boolean') {
          this.searchOpen = state.searchOpen
        }
        if ('searchKeyword' in this && typeof state.searchKeyword == 'string') {
          this.searchKeyword = state.searchKeyword
        }
        if ('dailyActiveTab' in this && ['todo', 'tasks', 'notes'].includes(state.dailyActiveTab)) {
          this.dailyActiveTab = state.dailyActiveTab
        }
      }
      if (shouldRestoreDailyFilters && 'dailyPeriod' in this && ['month', 'week', 'day'].includes(persistedState.dailyPeriod)) {
        this.dailyPeriod = persistedState.dailyPeriod
      }
      if (shouldRestoreDailyFilters && 'dailyTodoFinishFilter' in this && [-1, 0, 1].includes(Number(persistedState.dailyTodoFinishFilter))) {
        this.dailyTodoFinishFilter = Number(persistedState.dailyTodoFinishFilter)
      }
      if ('dailyViewMode' in this) {
        this.dailyViewMode = sharedViewMode
      }
      this.$nextTick(() => {
        this.cloudListStateRestoring = false
      })
    },
    saveCloudListState() {
      if (this.cloudListStateRestoring) {
        return
      }
      const appKey = this.getCloudListStateAppKey()
      if (!appKey) {
        return
      }
      setCloudAppState(appKey, {
        paperListResourceId: this.paperListResourceId || '',
        paperFolderPath: this.normalizeCloudPaperFolderPath(this.paperFolderPath),
        paperFilter: this.paperFilter || 'all',
        searchOpen: !!this.searchOpen,
        searchKeyword: this.searchKeyword || '',
        dailyActiveTab: this.dailyActiveTab || '',
        dailyPeriod: this.dailyPeriod || '',
        dailyTodoFinishFilter: this.dailyTodoFinishFilter,
        dailyViewMode: this.dailyViewMode || ''
      })
    },
    handleCloudViewModeChange(event) {
      const mode = normalizeCloudViewMode(event && event.detail ? event.detail.viewMode : '')
      if (this.viewMode != mode) {
        this.viewMode = mode
      }
      if ('dailyViewMode' in this && this.dailyViewMode != mode) {
        this.dailyViewMode = mode
      }
    },
    handleCloudSortChange(event) {
      const detail = event && event.detail ? event.detail : {}
      const sortState = {
        sortField: normalizeCloudSortField(detail.sortField),
        sortDesc: typeof detail.sortDesc == 'boolean' ? detail.sortDesc : true
      }
      if ('sortField' in this && this.sortField != sortState.sortField) {
        this.sortField = sortState.sortField
      }
      if ('sortDesc' in this && this.sortDesc != sortState.sortDesc) {
        this.sortDesc = sortState.sortDesc
      }
    }
  }
}
